import { useState, useEffect } from 'react';
import { getProducts, getCategories, getVideos, getBanners, LuxWebCache, API_BASE } from '../api/client';
import { socket } from '../api/socket';
import { Client } from '@stomp/stompjs';
import SockJS from 'sockjs-client';

export const sortCategories = (items = []) => {
    return [...items].sort((a, b) => {
        const aHome = a?.showOnHome === true ? 1 : 0;
        const bHome = b?.showOnHome === true ? 1 : 0;
        if (aHome !== bHome) return bHome - aHome;

        const aOrder = Number(a?.sortOrder ?? 9999);
        const bOrder = Number(b?.sortOrder ?? 9999);
        if (aOrder !== bOrder) return aOrder - bOrder;

        return String(a?.name || '').localeCompare(String(b?.name || ''));
    });
};

export const useStorefrontData = (showToast = () => {}) => {
    const [products, setProducts] = useState(() => LuxWebCache.getCachedProductList());
    const [categories, setCategories] = useState(() => sortCategories(LuxWebCache.getCachedCategoryList()));
    const [videos, setVideos] = useState(() => {
        const feedVideos = LuxWebCache.getCachedVideoList();
        const selectedForHome = feedVideos.filter(item => item?.showInGallery === true);
        return selectedForHome.length > 0 ? selectedForHome : feedVideos;
    });
    const [banners, setBanners] = useState(() => LuxWebCache.getCachedBannerList());
    const [loading, setLoading] = useState(() => {
        return LuxWebCache.getCachedProductList().length === 0 && LuxWebCache.getCachedCategoryList().length === 0;
    });

    const fetchAllData = (isSilent = false) => {
        if (!isSilent && products.length === 0 && categories.length === 0) {
            setLoading(true);
        }

        // Progressive Streaming + Live In-Place Delta Sync (Instagram & Alibaba architecture)
        getProducts(0, 24, '', '', '', '', '', { skipCache: isSilent })
            .then(p => {
                const safeProducts = Array.isArray(p?.data) ? p.data : [];
                if (safeProducts.length > 0) {
                    setProducts(prev => {
                        if (prev.length === 0 && safeProducts.length > 4) {
                            // Stream first 4 products in 0ms, then merge remainder on next frame
                            if (typeof window !== 'undefined' && window.requestAnimationFrame) {
                                window.requestAnimationFrame(() => setProducts(safeProducts));
                            } else {
                                setTimeout(() => setProducts(safeProducts), 0);
                            }
                            return safeProducts.slice(0, 4);
                        }
                        if (JSON.stringify(prev) === JSON.stringify(safeProducts)) {
                            return prev;
                        }
                        if (typeof window !== 'undefined') {
                            window.dispatchEvent(new CustomEvent('luxvono:realtime-catalog-sync', { detail: { products: safeProducts } }));
                        }
                        return safeProducts;
                    });
                    setLoading(false);
                    const nextThumbs = safeProducts
                        .slice(0, 12)
                        .map(item => item?.primaryImageUrl || item?.image || (Array.isArray(item?.images) ? item.images[0] : ''))
                        .filter(Boolean);
                    LuxWebCache.preloadImages(nextThumbs);
                }
            })
            .catch(err => console.error('Products sync failed:', err))
            .finally(() => {
                if (!isSilent) setLoading(false);
            });

        getCategories({ skipCache: isSilent })
            .then(c => {
                const allCategories = Array.isArray(c) ? c : [];
                if (allCategories.length > 0) {
                    const sortedCategories = sortCategories(allCategories);
                    setCategories(prev => JSON.stringify(prev) === JSON.stringify(sortedCategories) ? prev : sortedCategories);
                    setLoading(false);
                    LuxWebCache.preloadImages(sortedCategories.slice(0, 10).map(cat => cat?.imageUrl).filter(Boolean));
                }
            })
            .catch(() => {});

        getBanners(undefined, { skipCache: isSilent })
            .then(b => {
                if (Array.isArray(b)) {
                    setBanners(prev => JSON.stringify(prev) === JSON.stringify(b) ? prev : b);
                }
            })
            .catch(() => {});

        getVideos('', { skipCache: isSilent })
            .then(v => {
                const rawVideos = v?.data || v?.content || v || [];
                const feedVideos = Array.isArray(rawVideos) ? rawVideos : [];
                const selectedForHome = feedVideos.filter(item => item?.showInGallery === true);
                const activeVideos = selectedForHome.length > 0 ? selectedForHome : feedVideos;
                setVideos(prev => JSON.stringify(prev) === JSON.stringify(activeVideos) ? prev : activeVideos);
            })
            .catch(() => {});
    };

    // Initial Fetch, Silent 5s In-Place Delta Sync & STOMP Push Subscription
    useEffect(() => {
        fetchAllData(false);
        const interval = setInterval(() => fetchAllData(true), 5000);

        let stompClient = null;
        try {
            stompClient = new Client({
                webSocketFactory: () => new SockJS(`${API_BASE || ''}/ws/chat`),
                reconnectDelay: 4000,
                onConnect: () => {
                    stompClient.subscribe('/topic/products', () => fetchAllData(true));
                    stompClient.subscribe('/topic/categories', () => fetchAllData(true));
                    stompClient.subscribe('/topic/videos', () => fetchAllData(true));
                    stompClient.subscribe('/topic/config', () => fetchAllData(true));
                }
            });
            stompClient.activate();
        } catch (_) {}

        return () => {
            clearInterval(interval);
            try { stompClient?.deactivate(); } catch (_) {}
        };
    }, []);

    // Tab visibility and Socket Reconnection Syncing
    useEffect(() => {
        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') fetchAllData(true);
        };
        const handleSocketReconnect = () => fetchAllData(true);
        const handleSyncStorefrontData = (e) => {
            const { type } = e.detail || {};
            fetchAllData(true);
            if (type && showToast) {
                showToast(`🔥 Real-Time: ${type} updated successfully!`);
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);
        socket.on('connect', handleSocketReconnect);
        window.addEventListener('sync-storefront-data', handleSyncStorefrontData);

        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            socket.off('connect', handleSocketReconnect);
            window.removeEventListener('sync-storefront-data', handleSyncStorefrontData);
        };
    }, [showToast]);

    // Real-Time Subscriptions
    useEffect(() => {
        const handleNewProduct = (product) => {
            setProducts(prev => {
                if (prev.some(p => p.id === product.productId)) return prev;
                const newProduct = {
                    id: product.productId,
                    name: product.name,
                    price: product.price,
                    image: product.imagePath || product.image,
                    categoryName: product.category || product.categoryName,
                    isNewRealtime: true
                };
                return [newProduct, ...prev];
            });
            if (showToast) showToast(`🔥 Real-Time: Product "${product.name}" added instantly!`);
        };

        const handleProductUpdated = (product) => {
            setProducts(prev => {
                return prev.map(p => {
                    if (String(p.id) === String(product.productId || product.id)) {
                        return {
                            ...p,
                            ...product,
                            id: product.productId || product.id || p.id,
                            name: product.name || p.name,
                            price: product.price !== undefined ? product.price : p.price,
                            compareAtPrice: product.compareAtPrice !== undefined ? product.compareAtPrice : p.compareAtPrice,
                            image: product.imagePath || product.image || p.image,
                            categoryName: product.category || product.categoryName || p.categoryName
                        };
                    }
                    return p;
                });
            });
            if (showToast) showToast(`🔥 Real-Time: Product "${product.name || 'Updated'}" updated!`);
        };

        const handleProductDeleted = (product) => {
            const targetId = product.productId || product.id;
            setProducts(prev => prev.filter(p => String(p.id) !== String(targetId)));
            if (showToast) showToast(`🔥 Real-Time: Product removed!`);
        };

        const handleNewVideo = (video) => {
            setVideos(prev => {
                if (prev.some(v => v.id === video.videoId)) return prev;
                const newVideo = {
                    id: video.videoId,
                    productId: video.productId,
                    title: video.title,
                    videoUrl: video.videoUrl,
                    thumbnail: video.thumbnail,
                    showInGallery: true,
                    isNewRealtime: true
                };
                return [newVideo, ...prev];
            });
            if (showToast) showToast(`🔥 Real-Time: Video "${video.title}" processed & live!`);
        };

        const handleNewCategory = (cat) => {
            setCategories(prev => {
                if (prev.some(c => c.id === cat.categoryId)) return prev;
                const newCat = {
                    id: cat.categoryId,
                    name: cat.name,
                    slug: cat.slug,
                    imageUrl: cat.imageUrl,
                    showOnHome: cat.showOnHome,
                    sortOrder: cat.sortOrder,
                    isNewRealtime: true
                };
                return [...prev, newCat];
            });
            if (showToast) showToast(`🔥 Real-Time: Category "${cat.name}" added instantly!`);
        };

        const handleCategoryUpdated = (cat) => {
            setCategories(prev => {
                return prev.map(c => c.id === cat.categoryId ? {
                    ...c,
                    name: cat.name,
                    slug: cat.slug,
                    imageUrl: cat.imageUrl,
                    showOnHome: cat.showOnHome,
                    sortOrder: cat.sortOrder
                } : c);
            });
            if (showToast) showToast(`🔥 Real-Time: Category "${cat.name}" updated!`);
        };

        const handleCategoryDeleted = (cat) => {
            setCategories(prev => prev.filter(c => c.id !== cat.categoryId));
            if (showToast) showToast(`🔥 Real-Time: Category removed!`);
        };

        socket.on('new-product', handleNewProduct);
        socket.on('product-updated', handleProductUpdated);
        socket.on('product-deleted', handleProductDeleted);
        socket.on('new-video', handleNewVideo);
        socket.on('new-category', handleNewCategory);
        socket.on('category-updated', handleCategoryUpdated);
        socket.on('category-deleted', handleCategoryDeleted);

        return () => {
            socket.off('new-product', handleNewProduct);
            socket.off('product-updated', handleProductUpdated);
            socket.off('product-deleted', handleProductDeleted);
            socket.off('new-video', handleNewVideo);
            socket.off('new-category', handleNewCategory);
            socket.off('category-updated', handleCategoryUpdated);
            socket.off('category-deleted', handleCategoryDeleted);
        };
    }, [showToast]);

    return { products, categories, videos, banners, loading };
};
